import { useState, useEffect, useCallback } from "react"
import { toast } from "sonner"
import { useAnalytics } from "@/hooks/useAnalytics"
import {
  savePushSubscription,
  deletePushSubscription,
  hasPushSubscriptionRow,
  getPushPreferences,
  updatePushPreferences,
  type NotificationPreferencesRow,
  type NotificationPreferencesInput,
} from "@/lib/supabase"

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string

/**
 * État des notifications push SUR CET APPAREIL — navigateur ET base.
 *
 *   unsupported  pas de Web Push (Safari hors app installée sur iPhone…)
 *   denied       permission refusée : à réautoriser dans les réglages du téléphone
 *   active       abonnement dans le navigateur ET sa ligne dans push_subscriptions
 *   interrupted  abonnement dans le navigateur mais AUCUNE ligne en base : le serveur
 *                l'a supprimé (le service push a répondu 410 / 404) ou ne l'a jamais
 *                reçu (enregistrement raté). Aucun push ne peut partir.
 *   off          pas d'abonnement
 *
 * Jusqu'au 04/10, l'interrupteur ne lisait que le navigateur : il pouvait rester
 * vert avec une base vide, et le client ne recevait plus rien sans le savoir.
 */
export type PushState = "checking" | "unsupported" | "denied" | "active" | "interrupted" | "off"

export function usePushSubscription() {
  const isSupported = "PushManager" in window && "serviceWorker" in navigator
  const { track } = useAnalytics()

  const [permission, setPermission] = useState<NotificationPermission>(
    isSupported && typeof Notification !== "undefined" ? Notification.permission : "default"
  )
  const [state, setState] = useState<PushState>(isSupported ? "checking" : "unsupported")
  const [subscribing, setSubscribing] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreferencesRow | null>(null)

  useEffect(() => {
    if (!isSupported) return

    const init = async () => {
      try {
        const [reg, prefs] = await Promise.all([
          navigator.serviceWorker.ready,
          getPushPreferences(),
        ])
        setPreferences(prefs)

        if (Notification.permission === "denied") {
          setState("denied")
          return
        }
        const sub = await reg.pushManager.getSubscription()
        if (!sub) {
          setState("off")
          return
        }
        try {
          setState((await hasPushSubscriptionRow(sub.endpoint)) ? "active" : "interrupted")
        } catch (e) {
          // Lecture impossible (réseau) : on ne crie pas au loup sur une erreur passagère.
          console.error("[usePushSubscription] lecture de la ligne en base impossible :", e)
          setState("active")
        }
      } catch (e) {
        console.error("[usePushSubscription] init error:", e)
        setState("off")
      }
    }

    init()
  }, [isSupported])

  /**
   * Active les notifications — ou les RÉACTIVE quand l'état est « interrompues ».
   * Dans ce cas l'abonnement que garde le navigateur est mort (le service push l'a
   * refusé) : on le jette et on en crée un neuf. Appelée depuis un appui de
   * l'utilisateur, ce que l'iPhone exige pour s'abonner.
   */
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error(
        "Notifications non prises en charge. Sur iPhone, installe d'abord l'app sur ton écran d'accueil (Partager → Sur l'écran d'accueil).",
        { duration: 6000 }
      )
      return
    }

    setSubscribing(true)
    const wasInterrupted = state === "interrupted"
    try {
      if (Notification.permission === "default") {
        track('push_permission_requested')
      }
      const result = await Notification.requestPermission()
      setPermission(result)

      if (result === "denied") {
        setState("denied")
        toast.error(
          "Notifications bloquées. Autorise-les dans les réglages de ton téléphone ou de ton navigateur.",
          { duration: 6000 }
        )
        return
      }
      if (result !== "granted") return

      const reg = await navigator.serviceWorker.ready
      const stale = await reg.pushManager.getSubscription()
      if (stale) await stale.unsubscribe().catch(() => false)

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC_KEY,
      })

      try {
        await savePushSubscription(sub)
      } catch (e) {
        // Sans sa ligne en base, aucun push ne peut partir : on n'affiche pas un
        // interrupteur vert mensonger, on annule l'abonnement du navigateur.
        await sub.unsubscribe().catch(() => false)
        setState("off")
        throw e
      }

      track('push_subscribed', { reactivated: wasInterrupted })
      setState("active")
      toast.success(wasInterrupted ? "Notifications réactivées !" : "Notifications activées !")
    } catch (e) {
      console.error("[usePushSubscription] subscribe error:", e)
      toast.error("Impossible d'activer les notifications. Réessaie dans un instant.")
    } finally {
      setSubscribing(false)
    }
  }, [isSupported, state, track])

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        // La ligne d'abord : si la suppression en base échoue, l'abonnement reste
        // entier (et l'interrupteur vert), plutôt qu'une ligne orpheline en base.
        await deletePushSubscription(sub.endpoint)
        await sub.unsubscribe()
        track('push_unsubscribed')
      }
      setState("off")
      toast.success("Notifications désactivées.")
    } catch (e) {
      console.error("[usePushSubscription] unsubscribe error:", e)
      toast.error("Impossible de désactiver les notifications.")
    }
  }, [isSupported, track])

  const updatePrefs = useCallback(async (prefs: NotificationPreferencesInput) => {
    setPreferences((prev) => (prev ? { ...prev, ...prefs } : null))
    try {
      const updated = await updatePushPreferences(prefs)
      setPreferences(updated)
    } catch (e) {
      console.error("[usePushSubscription] updatePrefs error:", e)
      getPushPreferences().then(setPreferences).catch(() => null)
    }
  }, [])

  return {
    isSupported,
    permission,
    state,
    /** Notifications réellement actives sur cet appareil (navigateur ET base). */
    isSubscribed: state === "active",
    subscribing,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences: updatePrefs,
  }
}
